import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Mail,
  Plus,
  LayoutTemplate,
  Link2,
  Send,
  AlertCircle,
  Pencil,
  Eye,
  Trash2,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FloatingXScrollbar } from '../../components/table/FloatingXScrollbar'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { api, extractApiError } from '../../lib/api'
import { sampleForVariable } from '../../lib/email-sample-values'
import {
  emailTemplatesResponse,
  emailAssignmentsResponse,
} from '../../lib/types'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { TextField, FieldLabel } from '../../components/ui/Field'
import { formatDateTime } from '../../lib/format'

/** Shared input styling — mirrors `ui/Field.jsx`'s `inputBase`. */
const inputBase =
  'w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] ' +
  'bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm ' +
  'placeholder:text-[color:var(--color-ink-subtle)] outline-none ' +
  'transition-colors focus:border-[color:var(--color-accent)]'
const selectBase = `${inputBase} cursor-pointer`

function fmtDate(iso) {
  if (!iso) return '—'
  return formatDateTime(iso)
}

export function EmailTemplatesPage() {
  const [tab, setTab] = useState('templates')

  return (
    <div>
      <Breadcrumb current="Email Templates" homePath="/super-admin/dashboard" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Mail size={20} className="text-[color:var(--color-accent-strong)]" />
          Email Templates
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Edit every email the app sends.
        </p>
      </div>

      <div className="flex items-center gap-1 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5 w-fit mb-5">
        <TabButton
          active={tab === 'templates'}
          onClick={() => setTab('templates')}
          icon={LayoutTemplate}
          label="Templates"
        />
        <TabButton
          active={tab === 'assignments'}
          onClick={() => setTab('assignments')}
          icon={Link2}
          label="Assignments"
        />
      </div>

      {tab === 'templates' ? <TemplatesTab /> : null}
      {tab === 'assignments' ? <AssignmentsTab /> : null}
    </div>
  )
}

function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium cursor-pointer transition-colors ' +
        (active
          ? 'bg-[color:var(--color-primary)] text-white'
          : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
      }
    >
      <Icon size={14} />
      {label}
    </button>
  )
}

/** Pill on/off switch — used for template status and event activation. */
function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ' +
        // On = primary; in dark mode primary lifts to a bright blue, which
        // glares on the dark surface, so drop the on-track to a deeper blue.
        // Off already resolves to a dark slate (--color-rule-strong).
        (checked
          ? 'bg-[color:var(--color-primary)] dark:bg-blue-700'
          : 'bg-[color:var(--color-rule-strong)]')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ' +
          (checked ? 'translate-x-4' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

/* ------------------------------- Templates ------------------------------- */

function TemplatesTab() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  // Edit / create now navigates to a dedicated editor page so the
  // form gets full-page real estate. The earlier `editing` state +
  // TemplateDialog modal were removed when this swap landed —
  // `previewing` and `confirmDelete` stayed as modals because they
  // are read-only / single-confirm UX, much better suited to dialogs.
  const [previewing, setPreviewing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  // The actual-template test-send dialog. Distinct from the SMTP
  // tab's "Send test email" button — that one sends a fixed
  // diagnostic message to verify the SMTP connection. This one
  // renders the selected template with admin-supplied variable
  // values, so they can see exactly what a real recipient will get.
  const [testing, setTesting] = useState(null)

  const query = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const res = await api.get('/super-admin/email-templates')
      return emailTemplatesResponse.parse(res.data)
    },
  })

  const toggleMutation = useMutation({
    mutationFn: async (tpl) => {
      const res = await api.patch(`/super-admin/email-templates/${tpl.id}`, {
        isActive: !tpl.isActive,
      })
      return res.data
    },
    onSuccess: (_data, tpl) => {
      toast.success(tpl.isActive ? 'Template disabled.' : 'Template enabled.')
      void qc.invalidateQueries({ queryKey: ['email-templates'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const removeMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/super-admin/email-templates/${id}`)
      return id
    },
    onSuccess: () => {
      toast.success('Template deleted successfully.')
      setConfirmDelete(null)
      void qc.invalidateQueries({ queryKey: ['email-templates'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const rows = query.data?.data ?? []

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Template',
        grow: true,
        pinnable: true,
        minWidth: 240,
        sortAccessor: (t) => (t.name ?? '').toLowerCase(),
        searchAccessor: (t) => t.name ?? '',
        exportValue: (t) => t.name ?? '',
        render: (t) => (
          <span
            className="text-sm font-semibold text-[color:var(--color-ink-strong)] truncate"
            title={t.name}
          >
            {t.name}
          </span>
        ),
      },
      {
        key: 'assignedEvents',
        header: 'Assigned Event',
        minWidth: 180,
        sortAccessor: (t) => (t.assignedEvents ?? []).join(', ').toLowerCase(),
        searchAccessor: (t) => (t.assignedEvents ?? []).join(', '),
        exportValue: (t) => (t.assignedEvents ?? []).join(', '),
        cellClassName: 'text-sm text-[color:var(--color-ink-muted)]',
        render: (t) => (
          <span
            className="truncate block"
            title={
              t.assignedEvents?.length ? t.assignedEvents.join(', ') : undefined
            }
          >
            {t.assignedEvents?.length ? t.assignedEvents.join(', ') : '—'}
          </span>
        ),
      },
      {
        key: 'variables',
        header: 'Variables',
        minWidth: 100,
        sortAccessor: (t) => (t.variables ?? []).length,
        exportValue: (t) => (t.variables ?? []).length,
        cellClassName:
          'text-sm text-[color:var(--color-ink-muted)] tabular-nums',
        render: (t) =>
          `${(t.variables ?? []).length} variable${
            (t.variables ?? []).length === 1 ? '' : 's'
          }`,
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 140,
        sortAccessor: (t) => (t.isActive ? 0 : 1),
        exportValue: (t) => (t.isActive ? 'Active' : 'Disabled'),
        render: (t) => (
          <div className="flex items-center gap-2">
            <Toggle
              checked={t.isActive}
              disabled={toggleMutation.isPending}
              onChange={() => toggleMutation.mutate(t)}
              label={t.isActive ? 'Disable template' : 'Enable template'}
            />
            <span
              className={
                'text-xs font-medium ' +
                (t.isActive
                  ? 'text-[color:var(--color-success)]'
                  : 'text-[color:var(--color-ink-muted)]')
              }
            >
              {t.isActive ? 'Active' : 'Disabled'}
            </span>
          </div>
        ),
      },
      {
        key: 'updatedAt',
        header: 'Last Edited',
        minWidth: 150,
        sortAccessor: (t) => (t.updatedAt ? new Date(t.updatedAt) : null),
        exportValue: (t) => fmtDate(t.updatedAt),
        cellClassName:
          'text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (t) => fmtDate(t.updatedAt),
      },
      {
        key: 'actions',
        header: 'Actions',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (t) => (
          <RowActionsMenu
            label={`Actions for ${t.name}`}
            items={[
              {
                key: 'edit',
                label: 'Edit',
                icon: Pencil,
                onClick: () =>
                  navigate(`/super-admin/email-templates/${t.id}/edit`),
              },
              {
                key: 'preview',
                label: 'Preview',
                icon: Eye,
                onClick: () => setPreviewing(t),
              },
              {
                key: 'test',
                label: 'Send Test',
                icon: Send,
                onClick: () => setTesting(t),
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: Trash2,
                danger: true,
                separatorBefore: true,
                onClick: () => setConfirmDelete(t),
              },
            ]}
          />
        ),
      },
    ],
    [toggleMutation, navigate],
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          Create, edit and enable/disable the emails your app sends.
        </p>
        <button
          type="button"
          onClick={() => navigate('/super-admin/email-templates/new')}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer"
        >
          <Plus size={14} />
          New Template
        </button>
      </div>

      {/* Client-side templates list on the shared DataTable — search / sort /
       *  pagination / export in the browser. Status toggles inline; Edit /
       *  Preview / Send Test / Delete live in the row kebab. */}
      <DataTable
        tableId="platform-email-templates"
        columns={columns}
        rows={rows}
        getRowId={(t) => t.id}
        loading={query.isLoading}
        error={query.isError ? extractApiError(query.error).message : null}
        onRetry={() => query.refetch()}
        exportBaseName="email-templates"
        exportTitle="Email Templates"
        searchPlaceholder="Search Template or Event"
        defaultSort={[{ key: 'name', dir: 'asc' }]}
        emptyState={{
          icon: LayoutTemplate,
          title: 'No email templates yet',
          body: 'Create your first template, then wire it to a system event on the Assignments tab.',
        }}
      />

      {/* Edit / create previously mounted <TemplateDialog> here. It
       *  was retired in favour of a full-page editor at
       *  /super-admin/email-templates/new and /:id/edit so the form
       *  has room to breathe. The Preview / Delete confirms below
       *  stay as modals because they're read-only / single-confirm. */}

      {previewing ? (
        <PreviewDialog
          template={previewing}
          onClose={() => setPreviewing(null)}
        />
      ) : null}

      {testing ? (
        <SendTestDialog
          template={testing}
          onClose={() => setTesting(null)}
        />
      ) : null}

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Email Template"
        message={
          confirmDelete
            ? `Delete the "${confirmDelete.name}" template? Any event assigned to it will stop sending until you pick a replacement.`
            : ''
        }
        confirmLabel="Delete Template"
        variant="danger"
        confirming={removeMutation.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) removeMutation.mutate(confirmDelete.id)
        }}
      />
    </div>
  )
}

/* ----------------------------- Preview ---------------------------------- */

function buildPreviewDoc(bodyHtml) {
  // Highlight `{{var}}` placeholders inside text content only — never
  // inside an HTML tag's attribute (e.g. `src="{{platform_logo_url}}"`).
  // The naive replace-all approach wrapped placeholders in <span> even
  // when they sat inside an attribute value, producing invalid HTML
  // like `<img src="<span>...</span>">`. The browser then bailed on
  // the tag and spilled the rest of the attribute list into the page
  // text — which surfaced as the broken-image-icon + stray
  // `style="max-height:40px;..."` text the admin saw in the preview.
  //
  // The regex below alternates between matching a whole HTML tag
  // (passed through unchanged) OR a `{{var}}` token (wrapped in the
  // highlight span). Because tags are matched as a unit FIRST when
  // they appear, any placeholder inside a tag stays inside the tag
  // body and never gets wrapped.
  const inner = bodyHtml.trim()
    ? bodyHtml.replace(
        /(<[^>]*>)|(\{\{\s*[\w]+\s*\}\})/g,
        (_m, tag, placeholder) => {
          if (tag) return tag
          return `<span style="background:#fde68a;border-radius:3px;padding:0 3px">${placeholder}</span>`
        },
      )
    : '<p style="color:#94a3b8">This template has no body content.</p>'
  return `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:system-ui,-apple-system,'Segoe UI',sans-serif;margin:14px;color:#0f172a;font-size:13px;line-height:1.5}</style></head><body>${inner}</body></html>`
}

function PreviewDialog({ template, onClose }) {
  const previewDoc = useMemo(
    () => buildPreviewDoc(template.bodyHtml),
    [template.bodyHtml],
  )
  // Natural size of the rendered email, measured on iframe load. The
  // iframe never scrolls itself — its scrollbars are dead under
  // `pointer-events-none` (QA: "horizontal scroll bar not working").
  // The modal body scrolls vertically (single-scroller, mirroring the
  // Sent-email preview) and the wrapper div below scrolls horizontally.
  const [frameSize, setFrameSize] = useState({ width: null, height: 340 })
  return (
    <Modal
      open
      onClose={onClose}
      // `xl` (max-w-4xl) is wider than the 560–600px email templates, so the
      // preview fits without the wrapper needing a horizontal scrollbar —
      // leaving only the expected vertical scroll for tall emails. At `lg`
      // the fixed-width email overflowed and produced a second (horizontal)
      // scrollbar (QA: "why 2 scroll bars").
      size="xl"
      title={`Preview — ${template.name}`}
      description="Highlighted spans show where dynamic variables are substituted."
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="space-y-3">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Subject
          </p>
          <p className="text-sm text-[color:var(--color-ink-strong)]">
            {template.subject}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Body
          </p>
          <div className="rounded-lg border border-[color:var(--color-rule)] overflow-x-auto bg-white">
            {/* pointer-events-none — previews are non-interactive; the
             *  template CTAs would otherwise be clickable (same QA
             *  ruling as the Sent-email preview). That also makes the
             *  iframe's own scrollbars inert, so the iframe is sized to
             *  the email's natural width/height (measured on load via
             *  `allow-same-origin` — no scripts, same posture as the
             *  Sent-email preview) and the wrapper div above owns the
             *  horizontal scroll; the modal body scrolls vertically. */}
            <iframe
              title="Email body preview"
              sandbox="allow-same-origin"
              srcDoc={previewDoc}
              onLoad={(e) => {
                const doc = e.target.contentDocument?.documentElement
                if (!doc) return
                const width = doc.scrollWidth
                const height = doc.scrollHeight
                setFrameSize((prev) =>
                  prev.width === width && prev.height === height
                    ? prev
                    : { width, height },
                )
              }}
              style={{
                width: frameSize.width ?? '100%',
                minWidth: '100%',
                height: frameSize.height,
              }}
              className="block min-h-[340px] pointer-events-none"
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

/* --------------------------- Send test dialog --------------------------- */

/**
 * Per-template "Send test" dialog. Hits
 * `POST /super-admin/email-templates/:id/test` with a recipient email
 * and a `{ name → value }` map of variable values, so the recipient sees
 * the FINAL rendered template — not the diagnostic message that the
 * SMTP-tab "test email" button sends. This is the right tool for
 * verifying that the actual welcome / reset / invite email looks the
 * way the admin intends before any user triggers the underlying event.
 *
 * Variables are seeded with a sensible placeholder ("Sample {{name}}")
 * so the admin can hit Send immediately and still see something
 * recognisable in their inbox — they can override any of them inline.
 */
function SendTestDialog({ template, onClose }) {
  const [recipient, setRecipient] = useState('')
  const [values, setValues] = useState(() => {
    const out = {}
    for (const v of template.variables ?? []) {
      // Seed each variable with a REALISTIC sample value (real names,
      // dates, URLs, etc.) so a test send straight away reads like a
      // production email — not "Hi Sample admin_name,". Lookup is
      // shared with the editor preview pane so both surfaces agree.
      out[v] = sampleForVariable(v)
    }
    return out
  })
  const [recipientError, setRecipientError] = useState(null)

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        `/super-admin/email-templates/${template.id}/test`,
        { recipient: recipient.trim(), variables: values },
      )
      return res.data
    },
    onSuccess: () => {
      toast.success(`Test email sent to ${recipient.trim()}.`)
      onClose()
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields?.recipient) setRecipientError(fields.recipient)
      else toast.error(message)
    },
  })

  function onSubmit(e) {
    e.preventDefault()
    if (sendMutation.isPending) return
    if (!recipient.trim()) {
      setRecipientError('Recipient Email ID is required.')
      return
    }
    setRecipientError(null)
    sendMutation.mutate()
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={`Send test — ${template.name}`}
      description="Sends the fully-rendered template (subject + body) to the recipient below, using the variable values you provide."
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={sendMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            form="email-template-test-form"
            type="submit"
            loading={sendMutation.isPending}
          >
            <Send size={14} />
            Send Test Email
          </Button>
        </>
      }
    >
      <form
        id="email-template-test-form"
        onSubmit={onSubmit}
        noValidate
        className="space-y-4"
      >
        <TextField
          label="Recipient Email ID"
          required
          type="email"
          placeholder="Enter the Recipient Email ID"
          value={recipient}
          onChange={(e) => {
            setRecipient(e.target.value)
            if (recipientError) setRecipientError(null)
          }}
          error={recipientError}
          helperText="Use your own address so you can see exactly what the recipient will receive."
        />

        {(template.variables ?? []).length > 0 ? (
          <div>
            <FieldLabel label="Variable Values" />
            <p className="-mt-0.5 mb-2 text-xs text-[color:var(--color-ink-muted)]">
              These replace the {`{{placeholders}}`} in the subject and body.
              Edit any value to see how the email renders with different
              content.
            </p>
            <div className="space-y-2">
              {(template.variables ?? []).map((v) => (
                <div
                  key={v}
                  className="grid grid-cols-[140px_minmax(0,1fr)] items-center gap-3"
                >
                  <span className="text-xs font-mono font-medium text-[color:var(--color-accent-strong)] truncate">
                    {`{{${v}}}`}
                  </span>
                  <input
                    type="text"
                    value={values[v] ?? ''}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, [v]: e.target.value }))
                    }
                    placeholder={`Value for ${v}`}
                    className={inputBase}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-[color:var(--color-ink-muted)]">
            This template doesn't declare any dynamic variables.
          </p>
        )}
      </form>
    </Modal>
  )
}

/* ----------------------------- Assignments ------------------------------ */

function AssignmentsTab() {
  const assignmentsQuery = useQuery({
    queryKey: ['email-assignments'],
    queryFn: async () => {
      const res = await api.get('/super-admin/email-assignments')
      return emailAssignmentsResponse.parse(res.data)
    },
  })
  const templatesQuery = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const res = await api.get('/super-admin/email-templates')
      return emailTemplatesResponse.parse(res.data)
    },
  })

  if (assignmentsQuery.isLoading || templatesQuery.isLoading) {
    return <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
  }
  if (assignmentsQuery.isError) {
    return (
      <ErrorBlock message={extractApiError(assignmentsQuery.error).message} />
    )
  }
  // Remount the editor whenever fresh server data arrives (initial load
  // + after a save) so its draft state re-initialises cleanly — no
  // setState-in-effect needed.
  return (
    <AssignmentsEditor
      key={assignmentsQuery.dataUpdatedAt}
      groups={assignmentsQuery.data.groups}
      templates={templatesQuery.data?.data ?? []}
    />
  )
}

/** Flatten the server payload into the editable shape, keyed by event. */
function seedAssignments(groups) {
  const flat = {}
  for (const g of groups) {
    for (const e of g.events) {
      flat[e.key] = { templateId: e.templateId ?? '', isActive: e.isActive }
    }
  }
  return flat
}

/** Fixed-order tuple so the dirty check doesn't depend on key order. */
function canonicalAssignments(d) {
  return JSON.stringify(
    Object.keys(d)
      .sort()
      .map((k) => [k, d[k].templateId, d[k].isActive]),
  )
}

function AssignmentsEditor({ groups, templates }) {
  // Scroll container for the wide assignments grid — drives the floating
  // horizontal scrollbar pinned to the viewport bottom.
  const gridScrollRef = useRef(null)
  const qc = useQueryClient()
  const [draft, setDraft] = useState(() => seedAssignments(groups))
  // The editor REMOUNTS whenever fresh server data arrives (see the `key` on
  // <AssignmentsEditor>), so this first value is exactly what the server
  // returned — a safe baseline for the dirty check, and it re-seeds itself
  // after every successful save.
  const [serverSnapshot] = useState(() => seedAssignments(groups))
  // Toggling a row changes DRAFT state only; nothing is persisted until
  // "Save assignments". So the page must not claim success on toggle — it
  // has to show that there is something still to save (QA, 2026-09-01).
  const isDirty = useMemo(
    () => canonicalAssignments(draft) !== canonicalAssignments(serverSnapshot),
    [draft, serverSnapshot],
  )

  const saveMutation = useMutation({
    mutationFn: async () => {
      const assignments = Object.entries(draft).map(([eventKey, v]) => ({
        eventKey,
        templateId: v.templateId || null,
        isActive: v.isActive,
      }))
      const res = await api.put('/super-admin/email-assignments', {
        assignments,
      })
      return res.data
    },
    onSuccess: () => {
      toast.success('Assignments saved successfully.')
      void qc.invalidateQueries({ queryKey: ['email-assignments'] })
      void qc.invalidateQueries({ queryKey: ['email-templates'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function setEvent(key, patch) {
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }))
  }

  const cols =
    // Real column minimums + a grid min-width so the two text columns don't
    // collapse to one-word-per-line on a phone; the wrapper scrolls
    // horizontally instead. `lg:min-w-0` keeps the desktop layout unchanged
    // (mirrors the Templates tab grid).
    'grid grid-cols-[minmax(160px,1fr)_minmax(220px,1.4fr)_260px_70px] items-center gap-3 min-w-[820px] lg:min-w-0'

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          Connect each module event to the template that should be sent —
          and switch the whole event on or off.
        </p>
        <div className="flex items-center gap-3 shrink-0">
          {/* The only honest feedback for a toggle: it says the change is
            *  PENDING, not saved. A success toast here would claim a write
            *  that has not happened. */}
          {isDirty ? (
            <span
              role="status"
              className="text-xs font-medium text-[color:var(--color-priority-urgent)]"
            >
              Unsaved changes
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save assignments'}
          </button>
        </div>
      </div>

      <div
        ref={gridScrollRef}
        data-fd-xscroll
        className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-x-auto"
      >
          <div
            className={`${cols} px-4 py-2 text-sm text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]`}
          >
            <span>Event</span>
            <span>When It Fires</span>
            <span>Template</span>
            <span className="text-right">Active</span>
          </div>
          {groups.map((group) => (
            <div key={group.category}>
              <div className="px-4 py-1.5 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-subtle)] bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)]">
                {group.category}
              </div>
              {group.events.map((ev) => {
                const d = draft[ev.key] ?? { templateId: '', isActive: false }
                return (
                  <div
                    key={ev.key}
                    className={`${cols} px-4 py-3 border-b border-[color:var(--color-rule)] last:border-b-0`}
                  >
                    <span className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                      {ev.label}
                    </span>
                    <span className="text-sm text-[color:var(--color-ink-muted)]">
                      {ev.description}
                    </span>
                    <select
                      value={d.templateId}
                      onChange={(e) =>
                        setEvent(ev.key, {
                          templateId: e.target.value,
                          // Drop the active flag when nothing is chosen —
                          // the server enforces this too.
                          isActive: e.target.value ? d.isActive : false,
                        })
                      }
                      className={selectBase}
                    >
                      <option value="">— Not assigned —</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <div className="flex justify-end">
                      <Toggle
                        checked={d.isActive}
                        disabled={!d.templateId}
                        onChange={(next) =>
                          setEvent(ev.key, { isActive: next })
                        }
                        label={`Toggle ${ev.label}`}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      <FloatingXScrollbar containerRef={gridScrollRef} />
    </div>
  )
}

/* --------------------------- SMTP Settings ------------------------------ */

/* ----------------------------- Shared bits ------------------------------ */

function ErrorBlock({ message }) {
  return (
    <div className="rounded-xl border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] p-6 flex items-start gap-3">
      <AlertCircle
        size={18}
        className="shrink-0 mt-0.5 text-[color:var(--color-danger)]"
      />
      <div>
        <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
          Couldn't load this data
        </p>
        <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
          {message}
        </p>
      </div>
    </div>
  )
}
